/**
 * Auth context for the buyer portal (M12).
 *
 * Wraps the centralised api client (`services/api.js`) with React state
 * for the current user, the bearer token, and a `loading` flag the
 * routes use to gate their first render.
 *
 * Public surface:
 *   const { user, token, loading, login, register, logout, refreshUser } = useAuth();
 *
 * The `bc:auth:logout` window event lets api.js force a logout from a
 * 401 response without importing this module (the api → context import
 * cycle would otherwise be unavoidable).
 *
 * NOTE: the buyer portal historically stores the token under the
 * localStorage key `bidconvey_token`, NOT `token`. Preserved here so
 * existing logged-in sessions don't break on M12 deploy.
 */

import { createContext, useContext, useEffect, useState } from "react";

import api from "../services/api";

const TOKEN_KEY = "bidconvey_token";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(true);

  // Initial user fetch on mount or whenever the token changes.
  useEffect(() => {
    let cancelled = false;
    const fetchUser = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const res = await api.get("/auth/me");
        if (!cancelled) setUser(res.data);
      } catch (err) {
        if (!cancelled) {
          handleLogout();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchUser();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Listen for forced logouts from the api 401 interceptor.
  useEffect(() => {
    const onForcedLogout = () => handleLogout();
    window.addEventListener("bc:auth:logout", onForcedLogout);
    return () => window.removeEventListener("bc:auth:logout", onForcedLogout);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  };

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    const { access_token, user: userData } = res.data;
    localStorage.setItem(TOKEN_KEY, access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password) => {
    const res = await api.post("/auth/register", { name, email, password });
    const { access_token, user: userData } = res.data;
    localStorage.setItem(TOKEN_KEY, access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const refreshUser = async () => {
    const res = await api.get("/auth/me");
    setUser(res.data);
    return res.data;
  };

  const logout = handleLogout;

  return (
    <AuthContext.Provider
      value={{ user, token, loading, login, register, logout, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
